import React from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Position, spacing, Text } from '@cash-design-system/react'
import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import { RegionBlocker } from '@cash-web/protos/squareup/franklin/blockers/region_blocker.pb'
import { RegionBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetCountry } from '@cash-web/shared-data-access-api/react-query/useMutationSetCountry'
import { useRegion } from '@cash-web/shared-data-access-region-provider'
import { Flag, RadioGroup } from '@cash-web/shared-feature-form'

import CheckmarkChecked from '../../assets/icons/CheckmarkChecked'
import CheckmarkUnchecked from '../../assets/icons/CheckmarkUnchecked'
import { getSetCountryErrors } from '../../util/statusErrors'
import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../'
import { countryInfoMap } from './countryInfoMap'

interface Props extends BlockerProps {
  blocker: RegionBlocker
  supplement?: RegionBlockerSupplement
}

type RegionFields = BaseBlockerFormFields & {
  region: Country
}

const CountryLabel = styled.span`
  display: flex;
  align-items: center;
`

const StyledFlag = styled(Flag)`
  margin-right: ${spacing.m};
`

const RegionLabel = styled.h2`
  display: flex;
  ${({ theme }) => theme.typography.button};
  justify-content: space-between;
`

export function BlockerRegion({ blocker, supplement, skippable }: Props): React.ReactElement {
  const regionCode = useRegion()
  const country = regionCode ? Country[regionCode] : undefined
  const defaultRegion = country && blocker.suggested_countries?.includes(country) ? country : undefined
  const form = useForm<RegionFields>({
    defaultValues: {
      region: defaultRegion,
    },
  })
  const { watch } = form
  const watchRegion = watch('region')
  const { t } = useTranslation()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const { mutateAsync: setCountry } = useMutationSetCountry()
  async function onSubmit({ region }: RegionFields) {
    await handleBlockerResponse({
      promisedBlockerUpdate: setCountry({ country_code: region }),
      errors: getSetCountryErrors(t),
    })
  }

  const countryInfo = countryInfoMap(supplement?.display_countries)

  if (!countryInfo) {
    throw Error(
      `Missing region blocker supplement information for countries: ${blocker.suggested_countries?.join(', ')}`
    )
  }

  const options =
    blocker.suggested_countries?.map(code => {
      if (!countryInfo[code]) {
        throw Error(`Missing region blocker supplement information for country: ${code}`)
      }
      const countryName = countryInfo[code].country_name
      if (!countryName) {
        throw Error(`Missing country name in the region blocker supplement: ${code}`)
      }
      return {
        label: (
          <CountryLabel>
            <StyledFlag countryCode={code} />
            {countryName}
          </CountryLabel>
        ),
        value: code,
      }
    }) || []

  return (
    <BaseBlocker
      mainText={t('Confirm your country', { context: 'title' })}
      form={form}
      onSubmit={onSubmit}
      skippable={skippable}
    >
      <RadioGroup
        name="region"
        rules={{
          required: {
            value: true,
            message: t('A country is required', { context: 'error message' }),
          },
        }}
        options={options.map(({ label, value }) => {
          const isSelectedRatePlan = watchRegion === value
          return {
            label: (
              <RegionLabel>
                {label}
                <Position position="absolute" right="1rem">
                  <Text color="textBrand">{isSelectedRatePlan ? <CheckmarkChecked /> : <CheckmarkUnchecked />}</Text>
                </Position>
              </RegionLabel>
            ),
            value,
          }
        })}
      />
    </BaseBlocker>
  )
}
